import { useMyActivePlans } from 'app/network/plan';
import { useUser } from 'app/network/user';
import { useAllAddresses } from 'app/network/address';
import type { FreeSubSignalData, ResponseWithSignals } from 'app/network/signals';
import { getSignalData, hasSignal, Signal } from 'app/network/signals';
import type { SpecificNavigationProp, ScreenParameters } from 'app/types/navigation';

export function useTrialFreeSubAvailable<T extends keyof ScreenParameters>(
  navigation?: SpecificNavigationProp<T>,
): (response?: ResponseWithSignals) => {
  freeSubAvailable: undefined | false | FreeSubSignalData['program'];
  freeSubNumDaysLeft: undefined | false | number;
  freeSubNumOffersCreated: undefined | false | number;
  freeSubReceived: undefined | false | FreeSubSignalData['program'];
  shouldShowFreeSubScreen: boolean;
  navigateToFreeSubScreen: () => void;
} {
  const { refetch: refetchPlan } = useMyActivePlans({ enabled: false });
  const { data: addresses, refetch: refetchAddress } = useAllAddresses({ enabled: !navigation });
  return (response?: ResponseWithSignals) => {
    const resolvedResponse = response ?? addresses?.[0];

    let freeSubAvailable: undefined | false | FreeSubSignalData['program'] = undefined;
    let freeSubNumDaysLeft: undefined | false | number = undefined;
    let freeSubNumOffersCreated: undefined | false | number = undefined;
    let freeSubReceived: undefined | false | FreeSubSignalData['program'] = undefined;
    let userNowContributor: undefined | boolean = undefined;

    if (resolvedResponse) {
      if (hasSignal(resolvedResponse, Signal.TriggeredFreeSub)) {
        const signalData = getSignalData(resolvedResponse, Signal.TriggeredFreeSub) as FreeSubSignalData;
        freeSubReceived = signalData.program;
        void refetchAddress();
        void refetchPlan();
      } else {
        freeSubReceived = false;
      }
      userNowContributor = hasSignal(resolvedResponse, Signal.UserLevelNowContributor);
      if (hasSignal(resolvedResponse, Signal.FreeSubAvailable)) {
        const signalData = getSignalData(resolvedResponse, Signal.FreeSubAvailable) as FreeSubSignalData;
        freeSubAvailable = signalData.program;
        freeSubNumDaysLeft = signalData.numDaysLeft;
        freeSubNumOffersCreated = signalData.numOffers;
      } else {
        freeSubAvailable = false;
        freeSubNumDaysLeft = false;
        freeSubNumOffersCreated = false;
      }
    }

    const shouldShowFreeSubScreen = Boolean(navigation && (freeSubReceived || freeSubAvailable));

    const navigateToFreeSubScreen = () =>
      navigation?.navigate('freeSub', {
        userNowContributor: Boolean(userNowContributor),
        userGotFreeSub: freeSubReceived,
        numOffersCreated: freeSubNumOffersCreated,
      });

    return {
      freeSubAvailable,
      freeSubNumDaysLeft,
      freeSubNumOffersCreated,
      freeSubReceived,
      shouldShowFreeSubScreen,
      navigateToFreeSubScreen,
    };
  };
}

export function useReferralPrizeAvailable() {
  const { data: user } = useUser();
  return Boolean(user?.referralPrizeAvailable);
}

export function useWonReferralPrize() {
  const { data: user } = useUser();
  return user?.referralPrizeObtained;
}

export function useReferralPrize202506Available() {
  const { data: user } = useUser();
  return Boolean(user?.referralPrize202506Available);
}

export function useWonReferralPrize202506() {
  const { data: user } = useUser();
  return Boolean(user?.referralPrize202506Obtained);
}
